import { useNotification } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router";

import { isSeasonOnSale, seasonExamDate } from "@hedefuni/domain/plans/pricing";

import { api, ApiError } from "../api";
import { DATE_FORMAT } from "../labels";
import type { PlanSeason } from "../types";

type Values = { name: string; examDate: Dayjs };

/**
 * /plans/seasons (permission `plans.manage`; product decision 2026-10-11): exam seasons for
 * season plans ("2027 Dönemi"). A season plan runs from the purchase until the exam day is over
 * and does not renew; a purchase pays its weekly price for every week left. When the season
 * gets short, take its plans (or the 3-month ones) off sale on the plans page; they leave the
 * plans page by themselves when less than a week is left.
 */
export function PlanSeasonsPage() {
  const navigate = useNavigate();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  const [editing, setEditing] = useState<PlanSeason | "new" | null>(null);
  const [saving, setSaving] = useState(false);
  const {
    data: seasons,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["office", "seasons"],
    queryFn: () => api<PlanSeason[]>("/api/office/seasons"),
  });

  function edit(season: PlanSeason | "new") {
    setEditing(season);
    if (season === "new") form.resetFields();
    else {
      form.setFieldsValue({
        name: season.name,
        examDate: dayjs(seasonExamDate(new Date(season.endsAt))),
      });
    }
  }

  async function save() {
    const values = await form.validateFields();
    setSaving(true);
    const body = { name: values.name, examDate: values.examDate.format("YYYY-MM-DD") };
    try {
      if (editing === "new") await api("/api/office/seasons", { method: "POST", body });
      else if (editing) {
        await api(`/api/office/seasons/${encodeURIComponent(editing.id)}`, {
          method: "PATCH",
          body,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["office"] });
      setEditing(null);
      open?.({ type: "success", message: "Dönem kaydedildi." });
    } catch (cause) {
      open?.({
        type: "error",
        message:
          cause instanceof ApiError || cause instanceof Error ? cause.message : "Hata oluştu.",
      });
    } finally {
      setSaving(false);
    }
  }

  if (error) return <Alert type="error" showIcon message={error.message} />;

  return (
    <Card
      title="Dönemler"
      extra={
        <Space>
          <Button onClick={() => navigate("/plans")}>Planlar</Button>
          <Button type="primary" onClick={() => edit("new")}>
            Yeni dönem
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        Dönem paketleri satın alındığı günden sınav günü bitene kadar sürer ve yenilenmez. Satış
        fiyatı, planın haftalık fiyatı × sınava kalan hafta sayısıdır. Dönemi ekledikten sonra
        Planlar sayfasından 2, 4 ve 6 saatlik dönem planlarını oluştur.
      </Typography.Paragraph>
      <Table<PlanSeason>
        rowKey="id"
        loading={isLoading}
        dataSource={seasons ?? []}
        pagination={false}
        scroll={{ x: "max-content" }}
        columns={[
          { title: "Dönem", dataIndex: "name" },
          {
            title: "Sınav günü",
            key: "examDate",
            render: (_, season) =>
              dayjs(seasonExamDate(new Date(season.endsAt))).format(DATE_FORMAT),
          },
          {
            title: "Durum",
            key: "status",
            render: (_, season) =>
              isSeasonOnSale(new Date(season.endsAt), new Date()) ? (
                <Tag color="green">Satılabilir</Tag>
              ) : (
                <Tag>Bitti</Tag>
              ),
          },
          { title: "Plan", key: "plans", render: (_, season) => season._count?.plans ?? 0 },
          {
            title: "",
            key: "actions",
            render: (_, season) => <Button onClick={() => edit(season)}>Düzenle</Button>,
          },
        ]}
      />
      <Modal
        title={editing === "new" ? "Yeni dönem" : "Dönemi düzenle"}
        open={editing !== null}
        okText="Kaydet"
        cancelText="Vazgeç"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        destroyOnHidden
        forceRender
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Ad"
            rules={[{ required: true, message: "Dönemin adını yaz." }]}
            extra="Öğrenciler bu adı görür, ör. “2027 Dönemi”."
          >
            <Input maxLength={40} placeholder="2027 Dönemi" />
          </Form.Item>
          <Form.Item
            name="examDate"
            label="Sınav günü"
            rules={[{ required: true, message: "Sınav gününü seç." }]}
            extra="Paketler bu günün sonunda (İstanbul saatiyle) biter. Değiştirirsen önceden satılan paketlerin bitişi değişmez."
          >
            <DatePicker format={DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
